import type { AuthError, AuthRequest, AuthRequestParams, BaseClientInfo } from '@/features/mcp/types';

export const REQUIRED_PARAMS = ['clientId', 'redirectUri', 'responseType', 'codeChallenge'] as const;

export const getValueFromPayload = (payload: Record<string, string[]>, key: string): string | undefined => {
  return payload[key]?.[0];
};

export const parseAuthRequest = (
  authRequestParam: string,
): { request: AuthRequest; clientInfo: BaseClientInfo; payload: Record<string, string[]> } => {
  try {
    // Convert RAW URL-safe base64 to standard base64
    const base64 = authRequestParam.replace(/-/g, '+').replace(/_/g, '/');

    const decoded = atob(base64);
    const params = JSON.parse(decoded) satisfies AuthRequestParams;
    const { payload } = params;

    const clientInfo: BaseClientInfo = {
      clientId: getValueFromPayload(payload, 'client_id') ?? '',
      clientName: params.client_name,
      logoUri: params.logo_uri,
      clientUri: params.client_uri,
      tosUri: params.tos_uri,
      policyUri: params.policy_uri,
    };

    const request: AuthRequest = {
      redirectUri: getValueFromPayload(payload, 'redirect_uri') ?? '',
      responseType: getValueFromPayload(payload, 'response_type') ?? '',
      scope: getValueFromPayload(payload, 'scope'),
      state: getValueFromPayload(payload, 'state'),
      codeChallenge: getValueFromPayload(payload, 'code_challenge') ?? '',
      codeChallengeMethod: getValueFromPayload(payload, 'code_challenge_method'),
      ...clientInfo,
    };

    return {
      request,
      payload,
      clientInfo,
    };
  } catch (_error) {
    throw new Error('Invalid auth_request parameter. Failed to decode or parse.');
  }
};

export const getErrorMessage = (error: AuthError): string => {
  switch (error.type) {
    case 'missing_param': {
      const paramName = error.param.replace(/_/g, ' ');
      return `Missing required parameter: ${paramName}`;
    }
    case 'invalid_param': {
      return `Invalid parameter ${error.param}: ${error.message}`;
    }
    case 'api_error': {
      return error.message;
    }
    case 'parse_error': {
      return error.message;
    }
    default: {
      return 'An unknown error occurred';
    }
  }
};
